import { useCallback, useEffect, useRef, useState } from 'react';
import { Input, Popover, Divider } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import axios from 'axios';
import cn from 'classnames';
import { getTheme } from 'helpers/getTheme';
import { useAppSelector } from 'store/hook';
import api from 'api';
import IDocument from 'interfaces/IDocument';
import { ISearchContextResult } from 'interfaces/ISearch';
import SearchResultItem from './components/SearchResultItem';
import Spinner from 'components/Spinner/Spinner';
import classes from './ContentSearch.module.scss';

const ContentSearch = () => {
  const { t } = useTranslation();
  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { currentFolder } = useAppSelector((state) => state.documents);

  const [query, setQuery] = useState('');
  const [nameResults, setNameResults] = useState<IDocument[]>([]);
  const [contextResults, setContextResults] = useState<ISearchContextResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const cancelSourceRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);

  const currentFolderId = currentFolder[currentFolder.length - 1]?.id;

  const search = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery || searchQuery.trim().length < 1 || !dataRoom?.id) {
        setNameResults([]);
        setContextResults([]);
        setIsOpen(false);
        return;
      }

      if (cancelSourceRef.current) {
        cancelSourceRef.current.cancel();
      }
      cancelSourceRef.current = axios.CancelToken.source();
      const token = cancelSourceRef.current.token;

      setIsSearching(true);
      setIsOpen(true);

      try {
        const [nameRes, contentRes] = await Promise.allSettled([
          api.searchDocsAnFolders({ room_id: dataRoom.id, search: searchQuery }, token),
          api.searchDocumentsByContent(dataRoom.id, searchQuery, token, currentFolderId),
        ]);
        setNameResults((nameRes.status === 'fulfilled') ? nameRes.value.data.documents || [] : []);
        setContextResults((contentRes.status === 'fulfilled') ? contentRes.value.data || [] : []);
      } catch (err) {
        if (!axios.isCancel(err)) {
          setNameResults([]);
          setContextResults([]);
        }
      } finally {
        setIsSearching(false);
      }
    },
    [dataRoom?.id, currentFolderId]
  );

  const debouncedSearch = useDebouncedCallback(search, 300);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    if (value.trim().length >= 1) {
      setIsSearching(true);
      setIsOpen(true);
    } else {
      setNameResults([]);
      setContextResults([]);
      setIsOpen(false);
      setIsSearching(false);
    }
    debouncedSearch(value);
  };

  const handleClose = useCallback((e?: React.MouseEvent<HTMLSpanElement>) => {
    e?.stopPropagation();
    setIsOpen(false);
    setQuery('');
    setNameResults([]);
    setContextResults([]);
    setIsSearching(false);
    debouncedSearch.cancel();
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel();
      cancelSourceRef.current = null;
    }
  }, [debouncedSearch]);

  useEffect(() => {
    const close = (e: MouseEvent) => !wrapperRef.current?.contains(e.target as HTMLElement) && !(e.target as HTMLElement).closest(`.${classes.popoverOverlay}`) && setIsOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleClose();
    }
  };

  const hasResults = nameResults.length > 0 || contextResults.length > 0;
  const isEmpty = !isSearching && query.trim().length >= 1 && !hasResults;

  const popoverContent = (
    <div className={classes.popoverInner}>
      <div className={classes.popoverHeader}>
        <span className={classes.popoverHeaderTitle}>{t('Search.sections.name')}</span>
        <span className={classes.closeBtn} onClick={handleClose}>
          <CloseOutlined />
        </span>
      </div>

      <div className={classes.popoverBody}>
        {isSearching && (
          <div className={classes.loadingWrap}>
            <Spinner size={20} />
          </div>
        )}

        {!isSearching && isEmpty && (
          <div className={classes.emptyResults}>
            {t('Search.emptyResults', { query, interpolation: { escapeValue: false } })}
          </div>
        )}

        {!isSearching && hasResults && (
          <>
            {nameResults.length > 0 && (
              <div className={classes.sectionList}>
                {nameResults.map((doc) => (
                  <SearchResultItem key={doc.id} document={doc} searchQuery={query} onClose={handleClose} />
                ))}
              </div>
            )}

            {nameResults.length > 0 && contextResults.length > 0 && (
              <Divider className={classes.divider} />
            )}

            {contextResults.length > 0 && (
              <>
                <div className={classes.contextSectionHeader}>
                  {t('Search.sections.context')}
                </div>
                <div className={classes.sectionList}>
                  {contextResults.map((item) => (
                    <SearchResultItem
                      key={item.document.id}
                      document={item.document}
                      context={item.context}
                      onClose={handleClose}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
  return (
    <div ref={wrapperRef} className={classes.wrapper}>
      <Popover
        open={isOpen}
        onOpenChange={handleOpenChange}
        placement="bottomLeft"
        trigger={[]}
        arrow={false}
        content={isOpen ? popoverContent : null}
        classNames={{ root: cn(classes.popoverOverlay, getTheme()) }}
        destroyOnHidden
        align={{ points: ['tl', 'bl'], offset: [-24, 6], overflow: { adjustX: false, adjustY: true } }}
      >
        <Input
          className={cn(classes.searchInput, { [classes.searchInputActive]: isOpen })}
          prefix={<SearchOutlined className={classes.searchIcon} />}
          placeholder={t('Search.placeholder')}
          value={query}
          onChange={handleChange}
          onFocus={() => {
            if (query.trim().length >= 1 && hasResults) setIsOpen(true);
          }}
        />
      </Popover>
    </div>
  );
};

export default ContentSearch;
